import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Send } from 'lucide-react';
import { captureAttributionFromUrl } from '../utils/attribution';
import { trackCampaignEvent, registerUrlWithUtm } from '../utils/campaign';

const TEAM = [
  { name: 'Rebecca', role: 'Knowledge Tutor', img: '/avas/rebecca.png', desc: 'Socratic study help without judgment — she asks the next question until your teen finds the answer themselves.' },
  { name: 'Chloe', role: 'Friendships & Conflict', img: '/avas/chloe.jpg', desc: 'Peer pressure, group chats and social drama — rehearse the exact words before hitting "send" in real life.' },
  { name: 'Marcus', role: 'Careers & School', img: '/avas/marcus.jpg', desc: 'Speaking up to teachers, raising a mark dispute, and early interview practice — until it stops being scary.' },
];

const Shield = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
);

const CoParentShare = () => {
  const [copied, setCopied] = useState(false);
  const shareUrl = 'https://hibff.com/pre-launch?utm_source=coparent&utm_medium=share&utm_campaign=practice-share';
  const shareText = `Found something worth 15 minutes — HiBFF lets teens rehearse tough conversations (teachers, friend drama, interviews) with AI practice partners. Free practice sessions, no card needed: ${shareUrl}`;
  return (
    <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="coparent-share">
      <span className="text-sm text-white/50">Parenting decisions are shared —</span>
      <div className="flex gap-2">
        <a
          href={`mailto:?subject=${encodeURIComponent('Worth 15 minutes — free social practice for teens')}&body=${encodeURIComponent(shareText)}`}
          className="px-4 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-lg hover:bg-white/20 inline-flex items-center gap-2"
          data-testid="coparent-share-email"
        >
          <Send size={14} /> Send to your partner
        </a>
        <button
          onClick={() => { navigator.clipboard.writeText(shareText); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className="px-4 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-lg hover:bg-white/20"
          data-testid="coparent-share-copy"
        >
          {copied ? 'Copied ✓' : 'Copy link'}
        </button>
      </div>
    </div>
  );
};

const PreLaunch = () => {
  const navigate = useNavigate();

  useEffect(() => {
    captureAttributionFromUrl();
    trackCampaignEvent('landing_view', window.location.pathname);
  }, []);

  const startFree = (content) => {
    trackCampaignEvent('cta_click', window.location.pathname, { utm_content: content });
    navigate(registerUrlWithUtm());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950 text-white overflow-x-hidden" style={{ maxWidth: '100vw' }}>
      <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-56 sm:w-80 h-56 sm:h-80 bg-pink-500 rounded-full blur-[128px]" />
        <div className="absolute top-1/2 left-1/2 w-48 sm:w-64 h-48 sm:h-64 bg-blue-500 rounded-full blur-[96px]" />
      </div>

      <div className="relative z-10">
        <nav className="flex items-center justify-between gap-3 px-4 sm:px-6 py-5 max-w-6xl mx-auto flex-wrap">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-14 sm:h-24 flex-shrink-0" />
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/60 font-medium">
            <span className="flex items-center gap-1.5"><Shield /> COPPA</span>
            <span className="flex items-center gap-1.5"><Shield /> GDPR</span>
            <span className="flex items-center gap-1.5"><Shield /> SOC 2-ready</span>
          </div>
        </nav>

        {/* Hero */}
        <section className="px-4 pt-8 pb-16 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500/15 border border-emerald-400/30 rounded-full text-base sm:text-lg font-semibold text-emerald-300 mb-8 backdrop-blur-sm" data-testid="free-taster-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Free Practice Sessions — Available Now
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            The world doesn&apos;t need<br />
            another app that keeps<br />
            teens <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">staring at a screen.</span>
          </h1>

          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
            HiBFF is a low-stakes practice simulator where teens rehearse real-life conversations with AI coaches — so they can connect with confidence offline.
          </p>

          <button
            onClick={() => startFree('hero')}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-xl hover:shadow-xl hover:shadow-purple-500/25 transition-all inline-flex items-center gap-2"
            data-testid="hero-start-free-cta"
          >
            Start Practising Free — No Card Needed <ArrowRight size={20} />
          </button>
          <p className="text-sm text-white/50 mt-4" data-testid="hero-microcopy">
            15 free minutes &nbsp;•&nbsp; 3 specialised AvAs &nbsp;•&nbsp; £0 forever
          </p>

          {/* Founder Video + Quote */}
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 max-w-5xl mx-auto mt-14 mb-14">
            <div className="w-full max-w-[320px] sm:max-w-[360px] lg:w-5/12 lg:max-w-[400px] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 mx-auto" data-testid="founder-video">
              <div style={{ position: 'relative', paddingTop: '177.78%' }}>
                <iframe
                  src="https://player.mediadelivery.net/play/621268/a265f47e-d8fa-4a07-89d6-691886b7011e?autoplay=false&preload=true"
                  loading="lazy"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  title="HiBFF — Founder Introduction"
                />
              </div>
            </div>
            <div className="w-full lg:w-7/12 text-left px-2" data-testid="founder-quote">
              <div className="text-5xl font-bold text-orange-500 leading-none mb-6" style={{ fontFamily: 'Georgia, serif' }}>&quot;</div>
              <p className="text-xl sm:text-2xl font-semibold text-white leading-relaxed mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                HiBFF exists to help you need it less. It&apos;s a practice tool, not a replacement for real human connection.
              </p>
              <div>
                <p className="text-sm font-semibold text-white">Robert Parson</p>
                <p className="text-xs text-white/50">Founder & CEO, HiBFF</p>
              </div>
            </div>
          </div>

          {/* Meet Your Free Practice Team */}
          <div className="mb-14" data-testid="free-practice-team">
            <h2 className="text-2xl font-bold mb-2">Meet Your Free Practice Team</h2>
            <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
              Three of our nine specialised AvAs are free to try — a real 5-minute practice conversation with each, by text or voice.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {TEAM.map(a => (
                <div key={a.name} className="p-6 bg-gradient-to-b from-white/[0.08] to-white/[0.03] rounded-2xl border border-emerald-400/30 text-center" data-testid={`team-card-${a.name.toLowerCase()}`}>
                  <img src={a.img} alt={a.name} className="w-20 h-20 rounded-full object-cover mx-auto mb-4 ring-2 ring-emerald-400 ring-offset-2 ring-offset-gray-950" />
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 text-[10px] font-extrabold tracking-wider rounded-full bg-emerald-500 text-white"><Check size={10} /> TRY FOR FREE</div>
                  <h3 className="text-base font-bold text-white mb-1">{a.name}</h3>
                  <p className="text-xs font-semibold text-purple-300 mb-2">{a.role}</p>
                  <p className="text-sm text-white/60">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div className="mb-14 max-w-3xl mx-auto" data-testid="how-it-works">
            <h2 className="text-2xl font-bold mb-8">How it works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { n: '1', t: 'Pick a practice guide', d: 'Rebecca, Chloe or Marcus — your teen chooses who to talk to.' },
                { n: '2', t: 'Rehearse the conversation', d: 'Text or voice, completely private, zero judgment.' },
                { n: '3', t: 'Step into real life', d: 'They walk into the classroom, group chat or interview already prepared.' },
              ].map(s => (
                <div key={s.n} className="p-5 bg-white/5 rounded-xl border border-white/10 text-left">
                  <div className="w-9 h-9 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center mb-3">{s.n}</div>
                  <h3 className="text-sm font-bold text-white mb-1">{s.t}</h3>
                  <p className="text-sm text-white/60">{s.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Who is this for */}
          <div className="mb-14">
            <h2 className="text-2xl font-bold mb-6">Built for everyone who wants to feel more confident</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-4xl mx-auto">
              {[
                { label: 'Teens', desc: 'Safe, age-appropriate, parent-approved', color: 'from-amber-500 to-orange-500' },
                { label: 'Parents', desc: 'See progress, never conversations', color: 'from-blue-500 to-indigo-500' },
                { label: 'Adults', desc: 'Dating, networking, workplace confidence', color: 'from-purple-500 to-pink-500' },
                { label: 'Schools', desc: 'Curriculum-ready social skills training', color: 'from-green-500 to-emerald-500' },
                { label: 'Workplaces', desc: 'Team communication and leadership', color: 'from-cyan-500 to-blue-500' },
              ].map(a => (
                <div key={a.label} className="px-5 py-3 bg-white/5 rounded-xl border border-white/10 text-center h-full flex flex-col justify-center">
                  <div className={`text-sm font-bold bg-gradient-to-r ${a.color} bg-clip-text text-transparent`}>{a.label}</div>
                  <p className="text-xs text-white/50 mt-1">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Rewards (below the fold, unchanged) */}
          <div className="mb-14 max-w-3xl mx-auto" data-testid="rewards-section">
            <h2 className="text-2xl font-bold mb-3">Earn Real Rewards as You Grow</h2>
            <p className="text-white/60 text-sm mb-8 max-w-xl mx-auto">
              Every step you take builds something real. HiBFF turns your progress into tangible digital rewards you actually own.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="p-6 bg-gradient-to-b from-white/[0.08] to-white/[0.03] rounded-2xl border border-white/10 text-center">
                <h3 className="text-base font-bold text-white mb-2">Badges</h3>
                <p className="text-sm text-white/60">Earn badges for every milestone — first conversation, weekly streaks, breakthrough moments, and more.</p>
              </div>
              <div className="p-6 bg-gradient-to-b from-white/[0.08] to-white/[0.03] rounded-2xl border border-white/10 text-center">
                <h3 className="text-base font-bold text-white mb-2">Scout Badges</h3>
                <p className="text-sm text-white/60">Earn a Scout Badge for every real-world challenge you complete — and from Challenge 10, each one mints a collectible NFT trophy into your personal Vault.</p>
              </div>
              <div className="p-6 bg-gradient-to-b from-white/[0.08] to-white/[0.03] rounded-2xl border border-white/10 text-center">
                <h3 className="text-base font-bold text-white mb-2">NFT Vault</h3>
                <p className="text-sm text-white/60">At 180 days your vault opens, revealing your entire NFT collection — minted on the blockchain and truly yours.</p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="max-w-xl mx-auto" data-testid="final-cta">
            <h2 className="text-3xl font-bold mb-3">15 Minutes of Real Social Practice.<br />Zero Risk.</h2>
            <button
              onClick={() => startFree('final')}
              className="mt-4 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-xl hover:shadow-xl hover:shadow-purple-500/25 transition-all inline-flex items-center gap-2"
              data-testid="final-start-free-cta"
            >
              Claim Your 3 Free Practice Sessions <ArrowRight size={20} />
            </button>
            <p className="text-xs text-white/40 mt-4">GDPR compliant · COPPA aligned · built to SOC 2 readiness standards</p>
            <CoParentShare />
          </div>
        </section>

        <footer className="px-4 py-8 border-t border-white/10 max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/30">HiBFF Ltd. All rights reserved.</p>
            <div className="flex items-center gap-4 text-xs text-white/30">
              <a href="/terms" className="hover:text-white/60">Terms &amp; Conditions</a>
              <a href="/privacy" className="hover:text-white/60">Privacy &amp; Data Safety Policy</a>
              <a href="/refund-policy" className="hover:text-white/60">Refund Policy</a>
              <span>COPPA · GDPR · SOC 2-ready</span>
            </div>
          </div>
          <p className="text-center text-xs text-white/20 mt-4">HiBFF exists to help you need it less.</p>
        </footer>
      </div>
    </div>
  );
};

export default PreLaunch;
